import React, { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  TrendingUp,
  TrendingDown,
  Droplets,
  Gauge,
  MapPin,
  AlertTriangle,
  DollarSign,
  BarChart3,
  Activity,
  Calendar,
  CheckCircle,
  Clock,
  Zap
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useProductionCalculator } from '../hooks/useProductionCalculator';

interface AnalyticsMobileProps {
  wells: any[];
  onSelectWell?: (well: any) => void;
  isDarkMode?: boolean;
  /** Optional calculated production values for each well */
  calculatedProduction?: Map<string, {
    netOilBBL: number;
    grossWaterBBL: number;
    waterCutPercent: number;
    oilVsYesterdayPercent?: number;
  }>;
  /** Whether to prefer calculated values over raw well values */
  useCalculatedValues?: boolean;
}

export default function AnalyticsMobile({ 
  wells, 
  onSelectWell, 
  isDarkMode = false,
  calculatedProduction: externalCalcProduction,
  useCalculatedValues = false
}: AnalyticsMobileProps) {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30d');
  
  // Production calculator hook - use utility functions for display calculations
  const { calculateWaterCutPercent } = useProductionCalculator();
  
  // Calculate production values internally when not provided externally
  // Uses simple tank-to-BBL conversions for display purposes
  const calculatedProduction = useMemo(() => {
    // Use external values if provided
    if (externalCalcProduction) return externalCalcProduction;
    
    // Calculate from tank data if available
    const results = new Map<string, {
      netOilBBL: number;
      grossWaterBBL: number;
      waterCutPercent: number;
      oilVsYesterdayPercent?: number;
    }>();
    
    wells.forEach(well => {
      try {
        const tankData = well.latestEntry?.tankGauges || well.tankGauges;
        if (tankData) {
          // Calculate oil from tank gauges
          let totalOilBBL = 0;
          let totalWaterInOilBBL = 0;
          (tankData.oil || []).forEach((t: any) => {
            const currentInches = t.topGauge || t.inches || 0;
            const previousInches = t.previousTopGauge || t.bottomGauge || 0;
            const bblPerInch = t.bblPerInch || 3.57;
            const levelChange = currentInches - previousInches;
            const barrelsProduced = levelChange * bblPerInch + (t.barrelsHauled || 0);
            totalOilBBL += Math.max(0, barrelsProduced);
            
            // Account for water in oil tanks (Kolor Kut)
            if (t.kolorKut?.waterCutInches > 0) {
              totalWaterInOilBBL += t.kolorKut.waterCutInches * bblPerInch;
            }
          });
          
          // Calculate water from water tanks
          let totalWaterBBL = 0;
          (tankData.water || []).forEach((t: any) => {
            const currentInches = t.topGauge || t.inches || 0;
            const previousInches = t.previousTopGauge || t.bottomGauge || 0;
            const bblPerInch = t.bblPerInch || 3.57;
            const levelChange = currentInches - previousInches;
            const barrelsProduced = levelChange * bblPerInch + (t.barrelsHauled || 0);
            totalWaterBBL += Math.max(0, barrelsProduced);
          });
          
          // Net oil excludes water detected in oil tanks
          const netOilBBL = Math.max(0, totalOilBBL - totalWaterInOilBBL);
          const grossWaterBBL = totalWaterBBL + totalWaterInOilBBL;
          const waterCutPercent = calculateWaterCutPercent(netOilBBL, grossWaterBBL);
          
          if (netOilBBL > 0 || grossWaterBBL > 0) {
            results.set(well.id, {
              netOilBBL: Math.round(netOilBBL * 100) / 100,
              grossWaterBBL: Math.round(grossWaterBBL * 100) / 100,
              waterCutPercent: Math.round(waterCutPercent * 10) / 10,
            });
          }
        }
      } catch (err) {
        // Fall back to raw values - handled in getWellProduction
      }
    });
    
    return results.size > 0 ? results : undefined;
  }, [wells, externalCalcProduction, calculateWaterCutPercent]);

  // Helper function to get production value for a well
  const getWellProduction = (well: any, field: 'oil' | 'water') => {
    if (calculatedProduction) {
      const calc = calculatedProduction.get(well.id);
      if (calc) {
        return field === 'oil' ? calc.netOilBBL : calc.grossWaterBBL;
      }
    }
    // Fall back to raw well values
    return field === 'oil' ? (well.oilProduction || 0) : (well.waterProduction || 0);
  };

  // Calculate comprehensive analytics from wells data
  const analytics = useMemo(() => {
    const activeWells = wells.filter(w => w.status === 'active');
    const maintenanceWells = wells.filter(w => w.status === 'maintenance');
    const inactiveWells = wells.filter(w => w.status === 'inactive');

    // Total production - use calculated values when available
    const totalOil = wells.reduce((sum, w) => sum + getWellProduction(w, 'oil'), 0);
    const totalWater = wells.reduce((sum, w) => sum + getWellProduction(w, 'water'), 0);
    const totalGas = wells.reduce((sum, w) => sum + (w.staticPressure || 0), 0);

    // Average production per well
    const avgOil = wells.length > 0 ? totalOil / wells.length : 0;
    const avgWater = wells.length > 0 ? totalWater / wells.length : 0;

    // Top performers - sort by calculated or raw oil production
    const topOilWells = [...wells]
      .sort((a, b) => getWellProduction(b, 'oil') - getWellProduction(a, 'oil'))
      .slice(0, 5);

    // Underperforming wells (bottom 20%)
    const underperforming = [...wells]
      .filter(w => getWellProduction(w, 'oil') > 0)
      .sort((a, b) => getWellProduction(a, 'oil') - getWellProduction(b, 'oil'))
      .slice(0, Math.max(2, Math.floor(wells.length * 0.2)));

    // Wells needing attention (no recent entry)
    const needsAttention = wells.filter(well => {
      if (!well.lastEntry) return true;
      const daysSince = (Date.now() - new Date(well.lastEntry).getTime()) / (1000 * 60 * 60 * 24);
      return daysSince > 7;
    });

    // Calculate total maintenance costs from all entries
    let totalMaintenanceCost = 0;
    wells.forEach(well => {
      if (well.entries) {
        well.entries.forEach(entry => {
          totalMaintenanceCost += entry.maintenanceCost || 0;
        });
      }
    });

    // Production by location
    const locationMap = {};
    wells.forEach(well => {
      const loc = well.location || 'Unknown';
      if (!locationMap[loc]) {
        locationMap[loc] = {
          location: loc,
          wells: 0,
          oil: 0,
          water: 0,
          gas: 0
        };
      }
      locationMap[loc].wells += 1;
      locationMap[loc].oil += getWellProduction(well, 'oil');
      locationMap[loc].water += getWellProduction(well, 'water');
      locationMap[loc].gas += well.staticPressure || 0;
    });
    const locationData = Object.values(locationMap);

    // Production by company
    const companyMap = {};
    wells.forEach(well => {
      const company = well.company || 'Unknown';
      if (!companyMap[company]) {
        companyMap[company] = {
          company,
          wells: 0,
          oil: 0,
          water: 0
        };
      }
      companyMap[company].wells += 1;
      companyMap[company].oil += getWellProduction(well, 'oil');
      companyMap[company].water += getWellProduction(well, 'water');
    });
    const companyData = Object.values(companyMap);

    // Time series data - aggregate all entries
    const timeSeriesMap = {};
    wells.forEach(well => {
      if (well.entries) {
        well.entries.forEach(entry => {
          const date = new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          if (!timeSeriesMap[date]) {
            timeSeriesMap[date] = {
              date,
              oil: 0,
              water: 0,
              gas: 0,
              wells: 0
            };
          }
          timeSeriesMap[date].oil += entry.oilProduction || 0;
          timeSeriesMap[date].water += entry.waterProduction || 0;
          timeSeriesMap[date].gas += entry.staticPressure || 0;
          timeSeriesMap[date].wells += 1;
        });
      }
    });
    
    // Sort and average the time series data
    const timeSeriesData = Object.values(timeSeriesMap)
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((item: any) => ({
        date: item.date,
        oil: Math.round(item.oil / Math.max(item.wells, 1)),
        water: Math.round(item.water / Math.max(item.wells, 1)),
        gas: Math.round(item.gas / Math.max(item.wells, 1))
      }));

    // Status distribution for pie chart
    const statusData = [
      { name: 'Active', value: activeWells.length, color: '#22c55e' },
      { name: 'Maintenance', value: maintenanceWells.length, color: '#eab308' },
      { name: 'Inactive', value: inactiveWells.length, color: '#94a3b8' }
    ].filter(item => item.value > 0);

    // Calculate trends (compare recent vs older data)
    const halfwayPoint = Math.floor(timeSeriesData.length / 2);
    const olderData = timeSeriesData.slice(0, halfwayPoint);
    const recentData = timeSeriesData.slice(halfwayPoint);
    
    const oldAvgOil = olderData.length > 0 
      ? olderData.reduce((sum, d) => sum + d.oil, 0) / olderData.length 
      : 0;
    const recentAvgOil = recentData.length > 0 
      ? recentData.reduce((sum, d) => sum + d.oil, 0) / recentData.length 
      : 0;
    
    const oilTrend = oldAvgOil > 0 
      ? ((recentAvgOil - oldAvgOil) / oldAvgOil) * 100 
      : 0;

    const oldAvgWater = olderData.length > 0 
      ? olderData.reduce((sum, d) => sum + d.water, 0) / olderData.length 
      : 0;
    const recentAvgWater = recentData.length > 0 
      ? recentData.reduce((sum, d) => sum + d.water, 0) / recentData.length 
      : 0;
    
    const waterTrend = oldAvgWater > 0 
      ? ((recentAvgWater - oldAvgWater) / oldAvgWater) * 100 
      : 0;

    return {
      totalWells: wells.length,
      activeWells: activeWells.length,
      maintenanceWells: maintenanceWells.length,
      inactiveWells: inactiveWells.length,
      totalOil: Math.round(totalOil),
      totalWater: Math.round(totalWater),
      totalGas: Math.round(totalGas),
      avgOil: Math.round(avgOil * 10) / 10,
      avgWater: Math.round(avgWater * 10) / 10,
      topOilWells,
      underperforming,
      needsAttention,
      totalMaintenanceCost,
      locationData,
      companyData,
      timeSeriesData,
      statusData,
      oilTrend,
      waterTrend
    };
  }, [wells]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`flex flex-col h-full ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
      {/* Analytics Header - Fixed */}
      <div className={`${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'} border-b`}>
        <div className="p-4">
          <h2 className={`text-xl font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Analytics</h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{analytics.totalWells} wells monitored</p>
        </div>

        {/* iOS-Style Segmented Control for Subtabs */}
        <div className="px-4 pb-3">
          <div className={`${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'} rounded-lg p-1 grid grid-cols-4 gap-1`}>
            <button
              onClick={() => setActiveSubTab('overview')}
              className={`px-3 py-2 rounded-md font-medium text-sm transition-all ${
                activeSubTab === 'overview'
                  ? (isDarkMode ? 'bg-slate-700 text-white shadow-sm' : 'bg-white text-blue-600 shadow-sm')
                  : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600')
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveSubTab('production')}
              className={`px-3 py-2 rounded-md font-medium text-sm transition-all ${
                activeSubTab === 'production'
                  ? (isDarkMode ? 'bg-slate-700 text-white shadow-sm' : 'bg-white text-blue-600 shadow-sm')
                  : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600')
              }`}
            >
              Production
            </button>
            <button
              onClick={() => setActiveSubTab('wells')}
              className={`px-3 py-2 rounded-md font-medium text-sm transition-all ${
                activeSubTab === 'wells'
                  ? (isDarkMode ? 'bg-slate-700 text-white shadow-sm' : 'bg-white text-blue-600 shadow-sm')
                  : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600')
              }`}
            >
              Wells
            </button>
            <button
              onClick={() => setActiveSubTab('costs')}
              className={`px-3 py-2 rounded-md font-medium text-sm transition-all ${
                activeSubTab === 'costs'
                  ? (isDarkMode ? 'bg-slate-700 text-white shadow-sm' : 'bg-white text-blue-600 shadow-sm')
                  : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600')
              }`}
            >
              Costs
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className={`flex-1 overflow-y-auto ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
        {/* Overview Tab */}
        {activeSubTab === 'overview' && (
          <div className="p-4 space-y-3">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-blue-100 text-xs mb-1">Total Wells</p>
                      <p className="text-3xl font-semibold mb-1">{analytics.totalWells}</p>
                      <p className="text-blue-100 text-xs">{analytics.activeWells} active</p>
                    </div>
                    <MapPin className="h-8 w-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-green-100 text-xs mb-1">Oil Production</p>
                      <p className="text-3xl font-semibold mb-1">{analytics.totalOil}</p>
                      <p className="text-green-100 text-xs">BPD total</p>
                    </div>
                    <Droplets className="h-8 w-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-purple-100 text-xs mb-1">Water Production</p>
                      <p className="text-3xl font-semibold mb-1">{analytics.totalWater}</p>
                      <p className="text-purple-100 text-xs">BPD total</p>
                    </div>
                    <Gauge className="h-8 w-8 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-orange-100 text-xs mb-1">Needs Check</p>
                      <p className="text-3xl font-semibold mb-1">{analytics.needsAttention.length}</p>
                      <p className="text-orange-100 text-xs">overdue wells</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Status Distribution */}
            <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
              <CardContent className="p-4">
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Well Status Distribution</h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics.statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {analytics.statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDarkMode ? '#1e293b' : '#fff', 
                          borderColor: isDarkMode ? '#334155' : '#ccc', 
                          color: isDarkMode ? '#fff' : '#000' 
                        }} 
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="text-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-1"></div>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active</p>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{analytics.activeWells}</p>
                  </div>
                  <div className="text-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mx-auto mb-1"></div>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Maintenance</p>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{analytics.maintenanceWells}</p>
                  </div>
                  <div className="text-center">
                    <div className="w-3 h-3 bg-gray-400 rounded-full mx-auto mb-1"></div>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Inactive</p>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{analytics.inactiveWells}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trend Indicators */}
            <div className="grid grid-cols-2 gap-3">
              <Card className={isDarkMode ? 'bg-green-900/20 border-green-800' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'}>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>Oil Trend</span>
                  </div>
                  <p className={`text-2xl font-semibold ${analytics.oilTrend >= 0 ? (isDarkMode ? 'text-green-400' : 'text-green-900') : (isDarkMode ? 'text-red-400' : 'text-red-900')}`}>
                    {analytics.oilTrend >= 0 ? '+' : ''}{analytics.oilTrend.toFixed(1)}%
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-green-400/70' : 'text-green-600'}`}>vs previous period</p>
                </CardContent>
              </Card>

              <Card className={isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'}>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Activity className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Water Trend</span>
                  </div>
                  <p className={`text-2xl font-semibold ${analytics.waterTrend <= 0 ? (isDarkMode ? 'text-green-400' : 'text-green-900') : (isDarkMode ? 'text-orange-400' : 'text-orange-900')}`}>
                    {analytics.waterTrend >= 0 ? '+' : ''}{analytics.waterTrend.toFixed(1)}%
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-blue-400/70' : 'text-blue-600'}`}>vs previous period</p>
                </CardContent>
              </Card>
            </div>

            {/* Average Production */}
            <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
              <CardContent className="p-4">
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Average Per Well</h3>
                <div className="space-y-3">
                  <div className={`flex items-center justify-between py-2 border-b ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                    <div className="flex items-center space-x-2">
                      <Droplets className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Oil</span>
                    </div>
                    <span className={`font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{analytics.avgOil} BPD</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center space-x-2">
                      <Gauge className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Water</span>
                    </div>
                    <span className={`font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{analytics.avgWater} BPD</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Production Tab */}
        {activeSubTab === 'production' && (
          <div className="p-4 space-y-3">
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Production Trends</h3>

            {/* Time Series Chart */}
            <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
              <CardContent className="p-4">
                <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Average Daily Production</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={analytics.timeSeriesData}
                      margin={{
                        top: 5,
                        right: 10,
                        left: -20,
                        bottom: 5
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#eee'} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDarkMode ? '#94a3b8' : '#666' }} />
                      <YAxis tick={{ fontSize: 12, fill: isDarkMode ? '#94a3b8' : '#666' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDarkMode ? '#1e293b' : '#fff', 
                          borderColor: isDarkMode ? '#334155' : '#ccc', 
                          color: isDarkMode ? '#fff' : '#000' 
                        }} 
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="oil" 
                        stroke="rgb(34, 197, 94)" 
                        strokeWidth={2}
                        name="Oil (BPD)"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="water" 
                        stroke="rgb(59, 130, 246)" 
                        strokeWidth={2}
                        name="Water (BPD)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Production by Location */}
            <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
              <CardContent className="p-4">
                <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Production by Location</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analytics.locationData.slice(0, 5)}
                      margin={{
                        top: 5,
                        right: 10,
                        left: -20,
                        bottom: 5
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#eee'} />
                      <XAxis dataKey="location" tick={{ fontSize: 10, fill: isDarkMode ? '#94a3b8' : '#666' }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tick={{ fontSize: 12, fill: isDarkMode ? '#94a3b8' : '#666' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDarkMode ? '#1e293b' : '#fff', 
                          borderColor: isDarkMode ? '#334155' : '#ccc', 
                          color: isDarkMode ? '#fff' : '#000' 
                        }} 
                      />
                      <Legend />
                      <Bar dataKey="oil" fill="rgb(34, 197, 94)" name="Oil (BPD)" />
                      <Bar dataKey="water" fill="rgb(59, 130, 246)" name="Water (BPD)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Production by Company */}
            {analytics.companyData.length > 1 && (
              <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
                <CardContent className="p-4">
                  <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Production by Company</h4>
                  <div className="space-y-3">
                    {analytics.companyData.map((company: any, index) => (
                      <div key={index} className={`rounded-lg p-3 ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{company.company}</p>
                          <Badge variant="secondary">{company.wells} wells</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className={`rounded p-2 ${isDarkMode ? 'bg-green-900/20' : 'bg-green-100'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Oil</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-900'}`}>{Math.round(company.oil)} BPD</p>
                          </div>
                          <div className={`rounded p-2 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-100'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Water</p>
                            <p className={`font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>{Math.round(company.water)} BPD</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Wells Tab */}
        {activeSubTab === 'wells' && (
          <div className="p-4 space-y-3">
            {/* Top Performers */}
            <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <TrendingUp className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Top Producers</h3>
                </div>
                <div className="space-y-2">
                  {analytics.topOilWells.map((well, index) => (
                    <button
                      key={well.id}
                      onClick={() => onSelectWell && onSelectWell(well)}
                      className={`w-full rounded-lg p-3 border transition-colors text-left ${
                        isDarkMode 
                          ? 'bg-gradient-to-r from-green-900/20 to-slate-900 border-green-800 active:bg-green-900/30' 
                          : 'bg-gradient-to-r from-green-50 to-white border-green-200 active:bg-green-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-semibold">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{well.name}</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{well.location}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{Math.round(well.oilProduction)} BPD</p>
                          <Badge className={getStatusColor(well.status)} variant="secondary">
                            {well.status}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Needs Attention */}
            {analytics.needsAttention.length > 0 && (
              <Card className={isDarkMode ? 'bg-orange-900/10 border-orange-900/50' : 'border-orange-200 bg-orange-50'}>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <AlertTriangle className={`h-5 w-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                    <h3 className={`font-semibold ${isDarkMode ? 'text-orange-200' : 'text-orange-900'}`}>Needs Attention</h3>
                  </div>
                  <div className="space-y-2">
                    {analytics.needsAttention.slice(0, 5).map(well => {
                      const daysSince = well.lastEntry 
                        ? Math.floor((Date.now() - new Date(well.lastEntry).getTime()) / (1000 * 60 * 60 * 24))
                        : 999;
                      
                      return (
                        <button
                          key={well.id}
                          onClick={() => onSelectWell && onSelectWell(well)}
                          className={`w-full rounded-lg p-3 border transition-colors text-left ${
                            isDarkMode 
                              ? 'bg-slate-900 border-orange-900/50 active:bg-orange-900/20' 
                              : 'bg-white border-orange-200 active:bg-orange-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{well.name}</p>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-0.5`}>{well.location}</p>
                              <p className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'} mt-1`}>
                                {well.lastEntry ? `${daysSince} days since check` : 'Never checked'}
                              </p>
                            </div>
                            <Clock className={`h-5 w-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Underperforming Wells */}
            {analytics.underperforming.length > 0 && (
              <Card className={isDarkMode ? 'bg-yellow-900/10 border-yellow-900/50' : 'border-yellow-200 bg-yellow-50'}>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <TrendingDown className={`h-5 w-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                    <h3 className={`font-semibold ${isDarkMode ? 'text-yellow-200' : 'text-yellow-900'}`}>Low Production</h3>
                  </div>
                  <div className="space-y-2">
                    {analytics.underperforming.slice(0, 5).map(well => (
                      <button
                        key={well.id}
                        onClick={() => onSelectWell && onSelectWell(well)}
                        className={`w-full rounded-lg p-3 border transition-colors text-left ${
                          isDarkMode 
                            ? 'bg-slate-900 border-yellow-900/50 active:bg-yellow-900/20' 
                            : 'bg-white border-yellow-200 active:bg-yellow-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{well.name}</p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{well.location}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{Math.round(well.oilProduction)} BPD</p>
                            <Badge className={getStatusColor(well.status)} variant="secondary">
                              {well.status}
                            </Badge>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Costs Tab */}
        {activeSubTab === 'costs' && (
          <div className="p-4 space-y-3">
            {/* Total Maintenance Costs */}
            <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-indigo-100 text-sm mb-1">Total Maintenance</p>
                    <p className="text-3xl font-semibold mb-1">
                      ${analytics.totalMaintenanceCost.toLocaleString()}
                    </p>
                    <p className="text-indigo-100 text-xs">All time costs</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-indigo-200" />
                </div>
              </CardContent>
            </Card>

            {/* Cost Breakdown */}
            <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
              <CardContent className="p-4">
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Cost Analysis</h3>
                <div className="space-y-3">
                  <div className={`flex items-center justify-between py-2 border-b ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Avg Cost Per Well</span>
                    <span className={`font-semibold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                      ${Math.round(analytics.totalMaintenanceCost / Math.max(analytics.totalWells, 1)).toLocaleString()}
                    </span>
                  </div>
                  <div className={`flex items-center justify-between py-2 border-b ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Active Wells</span>
                    <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{analytics.activeWells}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Avg Cost Per Active Well</span>
                    <span className={`font-semibold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                      ${Math.round(analytics.totalMaintenanceCost / Math.max(analytics.activeWells, 1)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Efficiency Metrics */}
            <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
              <CardContent className="p-4">
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Production Efficiency</h3>
                <div className="space-y-3">
                  <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>Cost per Barrel (Oil)</span>
                      <Zap className={`h-4 w-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                    </div>
                    <p className={`text-2xl font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-900'}`}>
                      ${analytics.totalOil > 0 
                        ? (analytics.totalMaintenanceCost / analytics.totalOil).toFixed(2) 
                        : '0.00'}
                    </p>
                  </div>

                  <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Oil/Water Ratio</span>
                      <Activity className={`h-4 w-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <p className={`text-2xl font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                      {analytics.totalWater > 0 
                        ? (analytics.totalOil / analytics.totalWater).toFixed(2) 
                        : '0.00'}
                    </p>
                  </div>

                  <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>Production per Well</span>
                      <BarChart3 className={`h-4 w-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <p className={`text-2xl font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-900'}`}>
                      {analytics.avgOil} BPD
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cost Distribution by Location */}
            <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
              <CardContent className="p-4">
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Wells by Location</h3>
                <div className="space-y-2">
                  {analytics.locationData.slice(0, 5).map((loc: any, index) => (
                    <div key={index} className={`rounded-lg p-3 ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <MapPin className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{loc.location}</p>
                        </div>
                        <Badge variant="secondary">{loc.wells} wells</Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Total Production</span>
                        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {Math.round(loc.oil)} BPD oil
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
